import {
  Edge,
  SectionLabelNode,
  TileNode,
} from '@/components/diagrams/FlowChart';
import { AfterDeath } from '@/types/schema';
import { FlowChartGraph } from '@/utils/graphology/FlowChartGraph';

import {
  EstateWaterfall_NodeFragment,
  EstateWaterfallFragment,
} from '../graphql/EstateWaterfall.generated';

export type GraphEdgeType = 'default' | 'tax';
export enum GraphNodeCategorizationType {
  Individual = 'INDIVIDUAL',
  PersonalAccount = 'PERSONAL_ACCOUNT',
  InsuranceAccount = 'INSURANCE_ACCOUNT',
  FamilyGiving = 'FAMILY_GIVING',
  CharitableEntity = 'CHARITABLE_ENTITY',
  EstateTax = 'ESTATE_TAX',
  SectionLabel = 'SECTION_LABEL',
  GroupNode = 'GROUP_NODE',
  GiftTax = 'GIFT_TAX',
  ExternalSource = 'EXTERNAL_SOURCE',
}

export interface MinimumNodeData {
  id: string;
  afterDeath: AfterDeath;
}

export type EstateTaxNodeData = MinimumNodeData;

/** A single entity/record inside a waterfall group node. */
export interface GroupNodeMember {
  /** The underlying record id (entity id for openable members). */
  entityId: string;
  displayName: string;
  /**
   * Whether this member has an entity detail page and can be opened in a new
   * tab (true only for real `Entity` records — sub-trusts, individuals, etc.
   * have no standalone page).
   */
  isOpenableEntity: boolean;
}

/**
 * Attribute data for an aggregate group node. Shares {@link MinimumNodeData}'s
 * shape (no entity `node`) but carries the group's members so the presentation
 * editor can surface a "Group details" drawer without re-deriving them from
 * state.
 */
export interface GroupNodeData extends MinimumNodeData {
  members: GroupNodeMember[];
}

export type GraphNodeData =
  | EstateWaterfall_NodeFragment
  | EstateTaxNodeData
  | GroupNodeData;

export type GraphNode = TileNode | SectionLabelNode;

export interface EstateWaterfallGraphNodeAttributes<
  Node = GraphNode,
  Data = GraphNodeData,
> {
  node: Node;
  data: Data;
  categorizationType: GraphNodeCategorizationType;
}

export interface EstateWaterfallGraphEdgeAttributes {
  type: GraphEdgeType;
  edge: Edge;
}

export interface EstateWaterfallGraphAttributes {
  firstPrimaryClientDeathId: string;
  waterfall: EstateWaterfallFragment;
}

export type EstateWaterfallGraph = FlowChartGraph<
  EstateWaterfallGraphNodeAttributes,
  EstateWaterfallGraphEdgeAttributes,
  EstateWaterfallGraphAttributes
>;
